import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './FlushFrenzy.css';

// ═══════════════════════════════════════════════════════════════════════════
// FLUSH FRENZY - TOILET MODE EXCLUSIVE MINI-GAME
// 60-second fast-paced object identification game
// Flush toxic, keep clean - earn $DISTIL rewards!
// ═══════════════════════════════════════════════════════════════════════════

const GAME_DURATION = 60;
const OBJECTS_PER_GAME = 30;
const FREE_GAMES_PER_DAY = 3;

// Progressive difficulty: object delay shrinks as game progresses
// Starts at 800ms, ramps down to 350ms by object 25+
const getObjectDelay = (objectsProcessed) => {
  const progress = Math.min(objectsProcessed / 25, 1); // 0→1 over first 25 objects
  return Math.round(800 - (progress * 450)); // 800ms → 350ms
};
const EXTRA_GAME_COST = 100; // $DISTIL
const FLUSH_STORAGE_KEY = 'distil_flush_data';

const TOXIC_OBJECTS = [
  { name: 'ScamCoin', icon: '💀', desc: 'Obvious scam' },
  { name: 'Phishing NFT', icon: '🎣', desc: 'Suspicious link' },
  { name: 'Rug Pull', icon: '🪤', desc: 'Exit scam' },
  { name: 'Fake Airdrop', icon: '🎁', desc: 'Spam drop' },
  { name: 'Dust Attack', icon: '💩', desc: 'Spam dust' },
  { name: 'Malware', icon: '🦠', desc: 'Malicious code' },
  { name: 'Ponzi Token', icon: '🎰', desc: 'Pyramid scheme' },
  { name: 'Honeypot', icon: '🍯', desc: 'Trapped funds' },
];

const CLEAN_OBJECTS = [
  { name: 'SUI', icon: '💎', desc: 'Native token' },
  { name: 'NFT Badge', icon: '🎖️', desc: 'Your collection' },
  { name: 'USDC', icon: '💵', desc: 'Stablecoin' },
  { name: '$DISTIL', icon: '✨', desc: 'Protocol token' },
  { name: 'LP Token', icon: '🌊', desc: 'DeFi position' },
  { name: 'Art NFT', icon: '🎨', desc: 'Digital art' },
  { name: 'DAO Token', icon: '🏛️', desc: 'Governance' },
  { name: 'Game Asset', icon: '🎮', desc: 'Gaming NFT' },
];

const REWARD_TIERS = [
  { min: 0,   max: 200,   distil: 10,  label: 'Novice' },
  { min: 201, max: 400,   distil: 25,  label: 'Cleanser' },
  { min: 401, max: 600,   distil: 50,  label: 'Purifier' },
  { min: 601, max: 800,   distil: 100, label: 'Master' },
  { min: 801, max: 99999, distil: 150, label: 'LEGENDARY' },
];

// ─── PROP: isWalletConnected controls whether rewards are real or demo ───
function FlushFrenzy({ isOpen, onClose, onGameComplete, isWalletConnected = false, isToiletMode = false, purityScore = 0, onSpendDistil }) {
  const [gameState, setGameState] = useState('ready');

  // ── Daily limit state ──
  const [gamesPlayedToday, setGamesPlayedToday] = useState(0);
  const [freeGamesLeft, setFreeGamesLeft] = useState(FREE_GAMES_PER_DAY);
  const [showBuyPrompt, setShowBuyPrompt] = useState(false);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [score, setScore] = useState(0);
  const [currentObject, setCurrentObject] = useState(null);
  const [objectsProcessed, setObjectsProcessed] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [objectStartTime, setObjectStartTime] = useState(null);
  const [currentDelay, setCurrentDelay] = useState(800);

  const generateObject = useCallback(() => {
    const isToxic = Math.random() > 0.5;
    const pool = isToxic ? TOXIC_OBJECTS : CLEAN_OBJECTS;
    const obj = pool[Math.floor(Math.random() * pool.length)];
    return { ...obj, type: isToxic ? 'TOXIC' : 'CLEAN' };
  }, []);

  const startGame = (paid = false) => {
    // Track daily game count for wallet users
    if (isWalletConnected) {
      const today = new Date().toISOString().slice(0, 10);
      const data = JSON.parse(localStorage.getItem(FLUSH_STORAGE_KEY) || '{}');
      const played = data.date === today ? (data.gamesPlayed || 0) : 0;
      const newPlayed = played + 1;
      localStorage.setItem(FLUSH_STORAGE_KEY, JSON.stringify({ date: today, gamesPlayed: newPlayed }));
      setGamesPlayedToday(newPlayed);
      setFreeGamesLeft(Math.max(0, FREE_GAMES_PER_DAY - newPlayed));
    }
    setShowBuyPrompt(false);
    setCurrentDelay(800);
    setGameState('playing');
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setObjectsProcessed(0);
    setCorrectAnswers(0);
    setWrongAnswers(0);
    setCombo(0);
    setMaxCombo(0);
    setCurrentObject(generateObject());
    setObjectStartTime(Date.now());
  };

  const handleBuyGame = () => {
    if (purityScore < EXTRA_GAME_COST) return;
    if (onSpendDistil) onSpendDistil(EXTRA_GAME_COST);
    startGame(true);
  };

  // Reset to ready when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGameState('ready');
      setShowBuyPrompt(false);
    }
  }, [isOpen]);

  // Check daily game count on open
  useEffect(() => {
    if (!isOpen) return;
    // Demo mode: unlimited, no tracking
    if (!isWalletConnected) {
      setFreeGamesLeft(FREE_GAMES_PER_DAY);
      setGamesPlayedToday(0);
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const data = JSON.parse(localStorage.getItem(FLUSH_STORAGE_KEY) || '{}');
    const played = data.date === today ? (data.gamesPlayed || 0) : 0;
    setGamesPlayedToday(played);
    setFreeGamesLeft(Math.max(0, FREE_GAMES_PER_DAY - played));
  }, [isOpen, isWalletConnected]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setGameState('finished');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState]);

  const handleChoice = (choice) => {
    if (gameState !== 'playing' || !currentObject) return;

    const reactionMs = Date.now() - objectStartTime;
    const speedMult = reactionMs < 1000 ? 2.0 : reactionMs < 2000 ? 1.5 : 1.0;

    const isCorrect =
      (choice === 'flush' && currentObject.type === 'TOXIC') ||
      (choice === 'keep' && currentObject.type === 'CLEAN');

    let points = 0;
    let newCombo = combo;

    if (isCorrect) {
      points = Math.floor((choice === 'flush' ? 10 : 5) * speedMult);
      newCombo = combo + 1;
      setCombo(newCombo);
      setMaxCombo(Math.max(maxCombo, newCombo));
      setCorrectAnswers(correctAnswers + 1);
      if (newCombo >= 5) points += Math.floor(newCombo * 2);
      setFeedback({
        type: 'correct',
        message: choice === 'flush' ? '🚽 FLUSHED!' : '✅ KEPT!',
        points: `+${points}`,
        speedBonus: speedMult > 1 ? `${speedMult}x` : null,
        combo: newCombo >= 3 ? `${newCombo}x COMBO` : null,
      });
    } else {
      points = choice === 'flush' ? -15 : -10;
      newCombo = 0;
      setCombo(0);
      setWrongAnswers(wrongAnswers + 1);
      setFeedback({
        type: 'wrong',
        message: choice === 'flush' ? '😱 WAS CLEAN!' : '☠️ WAS TOXIC!',
        points: `${points}`,
      });
    }

    setScore(score + points);
    setObjectsProcessed(objectsProcessed + 1);

    const nextDelay = getObjectDelay(objectsProcessed + 1);
    setCurrentDelay(nextDelay);
    setTimeout(() => {
      setFeedback(null);
      if (objectsProcessed + 1 >= OBJECTS_PER_GAME || timeLeft <= 0) {
        setGameState('finished');
      } else {
        setCurrentObject(generateObject());
        setObjectStartTime(Date.now());
      }
    }, nextDelay);
  };

  const getFinalResults = () => {
    const accuracy = objectsProcessed > 0
      ? Math.round((correctAnswers / objectsProcessed) * 100)
      : 0;
    const tier = REWARD_TIERS.find(t => score >= t.min && score <= t.max) || REWARD_TIERS[0];

    let bonus = 0;
    const achievements = [];

    if (accuracy === 100 && objectsProcessed >= OBJECTS_PER_GAME) {
      bonus = 50;
      achievements.push({ id: 'perfect', name: 'Perfect Flush', icon: '💯', desc: '100% accuracy!' });
    }
    if (maxCombo >= 10) {
      achievements.push({ id: 'combo', name: 'Combo Master', icon: '🔥', desc: '10+ combo!' });
    }
    if (score >= 801) {
      achievements.push({ id: 'legend', name: 'Flush Legend', icon: '👑', desc: 'Legendary!' });
    }

    return {
      score: score + bonus,
      accuracy,
      objectsProcessed,
      correctAnswers,
      wrongAnswers,
      maxCombo,
      tier,
      bonus,
      achievements,
      distilReward: tier.distil + (bonus > 0 ? 25 : 0),
    };
  };

  const handleClaimReward = () => {
    const results = getFinalResults();

    if (isWalletConnected) {
      // ✅ REAL MODE: Fire callback so App.jsx can credit purity score immediately
      if (onGameComplete) onGameComplete(results);
    }
    // Demo mode: just close — nothing saved, nothing tracked

    onClose();
  };

  if (!isOpen) return null;

  const results = gameState === 'finished' ? getFinalResults() : null;

  return (
    <div className={`flush-frenzy-overlay${isToiletMode ? " toilet-mode" : ""}`} onClick={onClose}>
      <motion.div
        className={`flush-frenzy-modal${isToiletMode ? " toilet-mode" : ""}`}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: 'spring', damping: 25 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* READY SCREEN */}
        {gameState === 'ready' && (
          <div className="game-screen ready-screen">
            <div className="game-header">
              <div className="game-icon">🚽💨</div>
              <h2 className="game-title">FLUSH FRENZY</h2>
              <p className="game-subtitle">Toilet Mode Exclusive</p>
            </div>

            {/* Demo mode notice */}
            {!isWalletConnected && (
              <div className="demo-notice">
                <span className="demo-notice-icon">👁️</span>
                <span>Demo mode — connect wallet to earn real $DISTIL rewards</span>
              </div>
            )}

            <div className="game-instructions">
              <h3>How to Play:</h3>
              <div className="instruction-list">
                <div className="instruction-item">
                  <span className="instruction-icon">⚡</span>
                  <span>Objects appear - identify fast!</span>
                </div>
                <div className="instruction-item">
                  <span className="instruction-icon">🚽</span>
                  <span>FLUSH toxic objects (+10 pts)</span>
                </div>
                <div className="instruction-item">
                  <span className="instruction-icon">✅</span>
                  <span>KEEP clean objects (+5 pts)</span>
                </div>
                <div className="instruction-item">
                  <span className="instruction-icon">⚡</span>
                  <span>Speed bonus & combos!</span>
                </div>
              </div>
            </div>

            <div className="reward-preview">
              <h4>Rewards{!isWalletConnected ? ' (connect wallet to earn)' : ''}:</h4>
              <div className="reward-grid">
                {REWARD_TIERS.map(t => (
                  <div key={t.label} className="reward-tier">
                    {t.min}+ = {t.distil} $DISTIL
                  </div>
                ))}
              </div>
            </div>

            <div className="game-buttons">
              <button className="game-btn game-btn-secondary" onClick={onClose}>Close</button>
              {isWalletConnected ? (
                freeGamesLeft > 0 ? (
                  <div className="start-with-count">
                    <button className="game-btn game-btn-primary" onClick={() => startGame()}>🚀 START!</button>
                    <span className="games-left-badge">{freeGamesLeft} free {freeGamesLeft === 1 ? 'game' : 'games'} left today</span>
                  </div>
                ) : (
                  <div className="start-with-count">
                    <button
                      className="game-btn game-btn-buy"
                      onClick={handleBuyGame}
                      disabled={purityScore < EXTRA_GAME_COST}
                      title={purityScore < EXTRA_GAME_COST ? `Need ${EXTRA_GAME_COST} $DISTIL` : ''}
                    >
                      🎟️ Play for {EXTRA_GAME_COST} $DISTIL
                    </button>
                    <span className="games-left-badge empty">0 free games — resets midnight</span>
                  </div>
                )
              ) : (
                <button className="game-btn game-btn-primary" onClick={() => startGame()}>🚀 START!</button>
              )}
            </div>
          </div>
        )}

        {/* PLAYING SCREEN */}
        {gameState === 'playing' && (
          <div className="game-screen playing-screen">
            <div className="game-hud">
              <div className="hud-item">
                <span className="hud-label">SCORE</span>
                <span className="hud-value">{score}</span>
              </div>
              <div className="hud-item">
                <span className="hud-label">TIME</span>
                <span className={`hud-value ${timeLeft <= 10 ? 'warning' : ''}`}>{timeLeft}s</span>
              </div>
              <div className="hud-item">
                <span className="hud-label">COMBO</span>
                <span className="hud-value">{combo}x</span>
              </div>
              <div className="hud-item">
                <span className="hud-label">SPEED</span>
                <span className={`hud-value ${currentDelay <= 450 ? 'hud-speed-fast' : currentDelay <= 600 ? 'hud-speed-mid' : ''}`}>
                  {currentDelay <= 450 ? '🔥MAX' : currentDelay <= 600 ? '⚡FAST' : '🐢SLOW'}
                </span>
              </div>
            </div>

            <div className="game-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${(objectsProcessed / OBJECTS_PER_GAME) * 100}%` }} />
              </div>
              <span className="progress-text">{objectsProcessed} / {OBJECTS_PER_GAME}</span>
            </div>

            <AnimatePresence mode="wait">
              {currentObject && !feedback && (
                <motion.div
                  key={currentObject.name}
                  className="object-display"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: 180 }}
                  transition={{ type: 'spring', damping: 20 }}
                >
                  <div className="object-icon">{currentObject.icon}</div>
                  <div className="object-name">{currentObject.name}</div>
                  <div className="object-desc">{currentObject.desc}</div>
                </motion.div>
              )}
              {feedback && (
                <motion.div
                  className={`feedback-display ${feedback.type}`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  <div className="feedback-message">{feedback.message}</div>
                  <div className="feedback-points">{feedback.points}</div>
                  {feedback.speedBonus && <div className="feedback-bonus">{feedback.speedBonus}</div>}
                  {feedback.combo && <div className="feedback-combo">{feedback.combo}</div>}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="game-actions">
              <button className="action-btn keep-btn" onClick={() => handleChoice('keep')} disabled={!!feedback}>
                <span className="action-icon">✅</span>
                <span className="action-label">KEEP</span>
              </button>
              <button className="action-btn flush-btn" onClick={() => handleChoice('flush')} disabled={!!feedback}>
                <span className="action-icon">🚽</span>
                <span className="action-label">FLUSH</span>
              </button>
            </div>
          </div>
        )}

        {/* FINISHED SCREEN */}
        {gameState === 'finished' && results && (
          <div className="game-screen finished-screen">
            <div className="results-header">
              <div className="results-icon">{results.tier.label === 'LEGENDARY' ? '👑' : '🏆'}</div>
              <h2 className="results-title">GAME OVER!</h2>
              <p className="results-subtitle">{results.tier.label}</p>
            </div>

            <div className="results-score">
              <div className="final-score">
                <span className="score-label">SCORE</span>
                <span className="score-value">{results.score}</span>
              </div>
              <div className="reward-earned">
                <span className="reward-icon">💎</span>
                {isWalletConnected ? (
                  <span className="reward-amount">+{results.distilReward} $DISTIL</span>
                ) : (
                  <span className="reward-amount reward-amount-demo">
                    -- $DISTIL
                    <span className="reward-demo-label">Connect wallet to earn</span>
                  </span>
                )}
              </div>
            </div>

            <div className="results-stats">
              <div className="stat-item">
                <span className="stat-label">Accuracy</span>
                <span className="stat-value">{results.accuracy}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Correct</span>
                <span className="stat-value">{results.correctAnswers}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Wrong</span>
                <span className="stat-value">{results.wrongAnswers}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Max Combo</span>
                <span className="stat-value">{results.maxCombo}x</span>
              </div>
            </div>

            {results.achievements.length > 0 && (
              <div className="achievements-earned">
                <h4>Achievements!</h4>
                {results.achievements.map(ach => (
                  <div key={ach.id} className="achievement-item">
                    <span className="achievement-icon">{ach.icon}</span>
                    <div>
                      <div className="achievement-name">{ach.name}</div>
                      <div className="achievement-desc">{ach.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="game-buttons">
              {isWalletConnected ? (
                freeGamesLeft > 0 ? (
                  <button className="game-btn game-btn-secondary" onClick={() => startGame()}>🔄 Again ({freeGamesLeft} left)</button>
                ) : (
                  <button
                    className="game-btn game-btn-buy"
                    onClick={handleBuyGame}
                    disabled={purityScore < EXTRA_GAME_COST}
                  >
                    🎟️ Again for {EXTRA_GAME_COST} $DISTIL
                  </button>
                )
              ) : (
                <button className="game-btn game-btn-secondary" onClick={() => startGame()}>🔄 Again</button>
              )}
              <button className="game-btn game-btn-claim" onClick={handleClaimReward}>
                {isWalletConnected ? '✅ Claim Reward!' : '✅ Done'}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default FlushFrenzy;
